import React, { useState } from 'react';

interface TesterApplication {
  personalInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dateOfBirth: string;
    address: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
    };
  };
  kratomExperience: {
    yearsUsing: string;
    primaryReasons: string[];
    favoriteStrains: string[];
    experienceLevel: 'beginner' | 'intermediate' | 'advanced' | 'expert';
    dailyUsage: string;
  };
  writingSkills: {
    sampleReview: string;
    previousWriting: string;
    reviewExperience: string;
  };
  verification: {
    agreeToTerms: boolean;
    agreeToHonesty: boolean;
    agreeToTesting: boolean;
    idUpload?: File;
  };
}

export default function TrustedTesterApplication() {
  const [application, setApplication] = useState<TesterApplication>({
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      }
    },
    kratomExperience: {
      yearsUsing: '',
      primaryReasons: [],
      favoriteStrains: [],
      experienceLevel: 'beginner',
      dailyUsage: ''
    },
    writingSkills: {
      sampleReview: '',
      previousWriting: '',
      reviewExperience: ''
    },
    verification: {
      agreeToTerms: false,
      agreeToHonesty: false,
      agreeToTesting: false
    }
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const reasons = [
    'Pain Management', 'Anxiety Relief', 'Energy/Focus', 'Sleep Aid', 
    'Mood Enhancement', 'Opioid Replacement', 'General Wellness'
  ];

  const strains = [
    'Red Maeng Da', 'Green Maeng Da', 'White Maeng Da', 'Red Bali',
    'Green Bali', 'White Borneo', 'Red Thai', 'Green Thai', 'Gold/Yellow'
  ];

  const handlePersonalInfoChange = (field: string, value: string) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setApplication(prev => ({
        ...prev,
        personalInfo: {
          ...prev.personalInfo,
          [parent]: {
            ...prev.personalInfo.address,
            [child]: value
          }
        }
      }));
    } else {
      setApplication(prev => ({
        ...prev,
        personalInfo: {
          ...prev.personalInfo,
          [field]: value
        }
      }));
    }
  };

  const handleExperienceChange = (field: string, value: any) => {
    setApplication(prev => ({
      ...prev,
      kratomExperience: {
        ...prev.kratomExperience,
        [field]: value
      }
    }));
  };

  const handleWritingChange = (field: string, value: string) => {
    setApplication(prev => ({
      ...prev,
      writingSkills: {
        ...prev.writingSkills,
        [field]: value
      }
    }));
  };

  const handleVerificationChange = (field: string, value: boolean | File) => {
    setApplication(prev => ({
      ...prev,
      verification: {
        ...prev.verification,
        [field]: value
      }
    }));
  };

  const handleArrayChange = (field: string, value: string, checked: boolean) => {
    setApplication(prev => ({
      ...prev,
      kratomExperience: {
        ...prev.kratomExperience,
        [field]: checked 
          ? [...prev.kratomExperience[field as keyof typeof prev.kratomExperience] as string[], value]
          : (prev.kratomExperience[field as keyof typeof prev.kratomExperience] as string[]).filter(item => item !== value)
      }
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    // Simulate API submission
    setTimeout(() => {
      alert('Application submitted successfully! We will review your application and contact you within 5-7 business days.');
      setSubmitting(false);
    }, 2000);
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 4));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <h1 style={{ fontSize: '2.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem' }}>
          Trusted Tester Application
        </h1>
        <p style={{ color: '#6b7280', fontSize: '1.125rem' }}>
          Join our verified community of kratom reviewers
        </p>
      </div>

      {/* Progress Bar */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          {[1, 2, 3, 4].map(step => (
            <div key={step} style={{ 
              flex: '1', 
              textAlign: 'center',
              color: currentStep >= step ? '#3b82f6' : '#9ca3af',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}>
              Step {step}
            </div>
          ))}
        </div>
        <div style={{ 
          width: '100%', 
          height: '4px', 
          backgroundColor: '#e5e7eb', 
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div style={{ 
            width: `${(currentStep / 4) * 100}%`, 
            height: '100%', 
            backgroundColor: '#3b82f6',
            transition: 'width 0.3s ease'
          }}></div>
        </div>
      </div>

      <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {/* Step 1: Personal Information */}
        {currentStep === 1 && (
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', color: '#1f2937' }}>
              Personal Information
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  First Name *
                </label>
                <input
                  type="text"
                  value={application.personalInfo.firstName}
                  onChange={(e) => handlePersonalInfoChange('firstName', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    border: '1px solid #d1d5db', 
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  value={application.personalInfo.lastName}
                  onChange={(e) => handlePersonalInfoChange('lastName', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    border: '1px solid #d1d5db', 
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                  required
                />
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={application.personalInfo.email}
                  onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    border: '1px solid #d1d5db', 
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Phone *
                </label>
                <input
                  type="tel"
                  value={application.personalInfo.phone}
                  onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    border: '1px solid #d1d5db', 
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                  required
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Date of Birth * (Must be 21+)
              </label>
              <input
                type="date"
                value={application.personalInfo.dateOfBirth}
                onChange={(e) => handlePersonalInfoChange('dateOfBirth', e.target.value)}
                style={{ 
                  width: '200px', 
                  padding: '0.75rem', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
                required
              />
            </div>

            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', color: '#1f2937' }}>
              Address
            </h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Street Address *
                </label>
                <input
                  type="text"
                  value={application.personalInfo.address.street}
                  onChange={(e) => handlePersonalInfoChange('address.street', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    border: '1px solid #d1d5db', 
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                  required
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 150px', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    City *
                  </label>
                  <input
                    type="text"
                    value={application.personalInfo.address.city}
                    onChange={(e) => handlePersonalInfoChange('address.city', e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '0.75rem', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    State *
                  </label>
                  <input
                    type="text"
                    value={application.personalInfo.address.state}
                    onChange={(e) => handlePersonalInfoChange('address.state', e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '0.75rem', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    value={application.personalInfo.address.zipCode}
                    onChange={(e) => handlePersonalInfoChange('address.zipCode', e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '0.75rem', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Kratom Experience */}
        {currentStep === 2 && (
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', color: '#1f2937' }}>
              Kratom Experience
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Years Using Kratom *
                </label>
                <select
                  value={application.kratomExperience.yearsUsing}
                  onChange={(e) => handleExperienceChange('yearsUsing', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    border: '1px solid #d1d5db', 
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                  required
                >
                  <option value="">Select...</option>
                  <option value="<1">Less than 1 year</option>
                  <option value="1-2">1-2 years</option>
                  <option value="3-5">3-5 years</option>
                  <option value="5+">5+ years</option>
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Experience Level *
                </label>
                <select
                  value={application.kratomExperience.experienceLevel}
                  onChange={(e) => handleExperienceChange('experienceLevel', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    border: '1px solid #d1d5db', 
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                  required
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                  <option value="expert">Expert</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Daily Usage Pattern *
              </label>
              <input
                type="text"
                value={application.kratomExperience.dailyUsage}
                onChange={(e) => handleExperienceChange('dailyUsage', e.target.value)}
                placeholder="e.g., 2-3 grams morning, 3-4 grams evening"
                style={{ 
                  width: '100%', 
                  padding: '0.75rem', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
                required
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.75rem' }}>
                Primary Reasons for Use * (Select all that apply)
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
                {reasons.map(reason => (
                  <label key={reason} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                    <input
                      type="checkbox"
                      checked={application.kratomExperience.primaryReasons.includes(reason)}
                      onChange={(e) => handleArrayChange('primaryReasons', reason, e.target.checked)}
                    />
                    {reason}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.75rem' }}>
                Favorite Strains * (Select all that apply)
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.5rem' }}>
                {strains.map(strain => (
                  <label key={strain} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                    <input
                      type="checkbox"
                      checked={application.kratomExperience.favoriteStrains.includes(strain)}
                      onChange={(e) => handleArrayChange('favoriteStrains', strain, e.target.checked)}
                    />
                    {strain}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Writing Skills */}
        {currentStep === 3 && (
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', color: '#1f2937' }}>
              Writing Skills Assessment
            </h2>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Sample Review * (Write a detailed review of a kratom strain you've used recently - 150+ words)
              </label>
              <textarea
                value={application.writingSkills.sampleReview}
                onChange={(e) => handleWritingChange('sampleReview', e.target.value)}
                placeholder="Include strain name, vendor, effects experienced, quality, value for money, and any other relevant details..."
                style={{ 
                  width: '100%', 
                  height: '200px',
                  padding: '0.75rem', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  resize: 'vertical'
                }}
                required
              />
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                {application.writingSkills.sampleReview.length}/150 minimum characters
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Previous Writing Experience
              </label>
              <textarea
                value={application.writingSkills.previousWriting}
                onChange={(e) => handleWritingChange('previousWriting', e.target.value)}
                placeholder="Describe any previous writing experience (blogs, reviews, articles, etc.)"
                style={{ 
                  width: '100%', 
                  height: '100px',
                  padding: '0.75rem', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Review Platform Experience
              </label>
              <textarea
                value={application.writingSkills.reviewExperience}
                onChange={(e) => handleWritingChange('reviewExperience', e.target.value)}
                placeholder="Have you written reviews on other platforms? (Amazon, Google, Yelp, etc.)"
                style={{ 
                  width: '100%', 
                  height: '100px',
                  padding: '0.75rem', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>
        )}

        {/* Step 4: Verification & Agreement */}
        {currentStep === 4 && (
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', color: '#1f2937' }}>
              Verification & Agreement
            </h2>
            
            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Upload Government-Issued ID * (Driver's License, Passport, State ID)
              </label>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={(e) => e.target.files && handleVerificationChange('idUpload', e.target.files[0])}
                style={{ 
                  width: '100%', 
                  padding: '0.75rem', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
                required
              />
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                Your ID will be used for age verification only and stored securely
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  checked={application.verification.agreeToTerms}
                  onChange={(e) => handleVerificationChange('agreeToTerms', e.target.checked)}
                  style={{ marginTop: '2px' }}
                  required
                />
                <span>
                  I agree to the <strong>Trusted Tester Terms & Conditions</strong> and understand that I must provide honest, unbiased reviews regardless of any compensation received.
                </span>
              </label>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  checked={application.verification.agreeToHonesty}
                  onChange={(e) => handleVerificationChange('agreeToHonesty', e.target.checked)}
                  style={{ marginTop: '2px' }}
                  required
                />
                <span>
                  I commit to providing <strong>honest, detailed reviews</strong> based on my actual experience. I understand that deliberately false or misleading reviews will result in immediate removal from the program.
                </span>
              </label>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  checked={application.verification.agreeToTesting}
                  onChange={(e) => handleVerificationChange('agreeToTesting', e.target.checked)}
                  style={{ marginTop: '2px' }}
                  required
                />
                <span>
                  I understand that as a Trusted Tester, I may receive free product samples for testing purposes and that <strong>I am not required to write positive reviews</strong> in exchange for these products.
                </span>
              </label>
            </div>

            <div style={{ 
              padding: '1rem', 
              backgroundColor: '#eff6ff', 
              borderRadius: '0.375rem', 
              border: '1px solid #bfdbfe',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#1e40af', marginBottom: '0.5rem' }}>
                What Happens Next?
              </h3>
              <ul style={{ margin: '0', paddingLeft: '1rem', color: '#1e40af', fontSize: '0.875rem' }}>
                <li>We'll review your application within 5-7 business days</li>
                <li>If approved, you'll receive a Trusted Tester badge and access to the vendor connection platform</li>
                <li>You can start receiving testing requests from verified vendors</li>
                <li>All reviews are published with "Trusted Tester" verification</li>
              </ul>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: currentStep === 1 ? '#e5e7eb' : '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: currentStep === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>

          {currentStep < 4 ? (
            <button
              onClick={nextStep}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || !application.verification.agreeToTerms || !application.verification.agreeToHonesty || !application.verification.agreeToTesting}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: submitting ? '#9ca3af' : '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Application'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}